import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { UserInteractionApiService } from '@angular-workspace/services';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import {
  findHackathonDatasetForCatalogueItem,
  getCatalogueDatasetDetailLink,
  getReferenceDatasetDetailLink,
  HACKATHON_DATASETS,
} from '../../../catalogue/reference-datasets/hackathon-datasets.data';

export interface FeatureCardData {
  id: string;
  title: string;
  shortDescription?: string; // Short description of the asset, shown below the label
  organization: string;
  tags: string[]; // ResourceServer names for card display
  serverAccessTypes?: string[]; // Access types from resourceServer.accessTypes (e.g. ATTR, TEMPORAL)
  apiTags?: string[]; // Actual tags from API for Tags section
  percentage?: number;
  fileType?: string;
  date?: string;
  badge?: string;
  badgeColor?: 'public' | 'private' | 'restricted';
  isNew?: boolean;
  resourceCount?: number;
  link?: string;
  /** Reference dataset card fields (TANUH benchmark datasets). */
  problemStatement?: string;
  modality?: string;
  volume?: string;
  output?: string;
  primaryMetric?: string;
  secondPrimaryMetric?: string;
  secondaryMetric?: string;
  referenceDatasetKey?: string;
  metrics?: {
    likes?: number;
    downloads?: number;
    files?: number;
    views?: number;
  };
}

@Component({
  selector: 'app-feature-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './feature-card.component.html',
  styleUrl: './feature-card.component.scss',
})
export class FeatureCardComponent {
  @Input() card!: FeatureCardData;
  /** When true, short description is shown below the label (e.g. in list view). Default false for grid/table. */
  @Input() showShortDescription: boolean = false;
  /** When true, activity metrics (likes, views) are shown. Set false e.g. on Features of NHA landing. */
  @Input() showActivityMetrics: boolean = true;
  /** When true, show organization name prominently below the title (e.g. when Bookmarks view is active). */
  @Input() showOrganizationBelowTitle: boolean = false;
  @Input() isBookmarked: boolean = false;
  @Input() isLiked: boolean = false;
  @Input() isLoggedIn: boolean = false;
  @Output() bookmarkChanged = new EventEmitter<boolean>();
  @Output() likeChanged = new EventEmitter<boolean>();
  
  private router = inject(Router);
  private userInteractionApiService = inject(UserInteractionApiService);
  private googleAnalytics = inject(GoogleAnalyticsService);

  getBadgeClass(): string {
    if (!this.card.badgeColor) return 'feature-card__badge--public';
    return `feature-card__badge--${this.card.badgeColor}`;
  }

  onCardClick(): void {
    if (this.card?.link?.startsWith('/reference/dataset/')) {
      this.googleAnalytics.trackDatasetClick(this.card.title ?? '', this.card.id ?? '');
      void this.router.navigateByUrl(this.card.link);
      return;
    }

    if (this.card?.referenceDatasetKey) {
      const ds = HACKATHON_DATASETS.find((d) => d.id === this.card.referenceDatasetKey);
      if (ds) {
        this.googleAnalytics.trackDatasetClick(this.card.title ?? '', this.card.id ?? '');
        void this.router.navigateByUrl(getReferenceDatasetDetailLink(ds, 'datasets'));
        return;
      }
    }

    const matched = findHackathonDatasetForCatalogueItem(this.card);
    if (matched) {
      this.googleAnalytics.trackDatasetClick(this.card.title ?? '', this.card.id ?? '');
      void this.router.navigateByUrl(getReferenceDatasetDetailLink(matched, 'datasets'));
      return;
    }

    // Navigate to dataset detail page if id is available
    if (this.card?.id) {
      this.googleAnalytics.trackDatasetClick(this.card.title ?? '', this.card.id);
      void this.router.navigate(['/dataset', this.card.id], {
        queryParams: { from: 'datasets' },
      });
    } else if (this.card?.link) {
      console.log('Navigating to link:', this.card.link);
      this.router.navigateByUrl(this.card.link);
    } else {
      console.warn('No ID or link available for navigation. Card data:', this.card);
    }
  }

  getDisplayTag(tag: string): string {
    // Replace PII with Personal for UI display only (case-insensitive)
    if (!tag) return tag;
    return tag.toUpperCase() === 'PII' ? 'Personal' : tag;
  }

  shouldHideTag(tag: string): boolean {
    if (!tag) return false;
    const normalizedTag = tag.trim().toUpperCase();
    return normalizedTag === 'OGC' || normalizedTag === 'ATTR';
  }

  /**
   * Handle bookmark icon click in metrics section
   */
  async onBookmarkIconClick(event: Event): Promise<void> {
    event.stopPropagation(); // Prevent card click navigation
    
    if (!this.isLoggedIn) {
      // Emit event to show login modal (handled by parent)
      return;
    }

    if (!this.card?.id) {
      console.error('Cannot bookmark: card ID is missing');
      return;
    }

    try {
      const actionType = this.isBookmarked ? 'UNBOOKMARK' : 'BOOKMARK';
      
      const payload = {
        assetId: this.card.id,
        assetType: 'DATABANK',
        actionType: actionType
      };

      await this.userInteractionApiService.createBookmarkOrLike(payload);
      
      // Update local state
      this.isBookmarked = !this.isBookmarked;
      this.bookmarkChanged.emit(this.isBookmarked);
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  /**
   * Handle like icon click in metrics section
   */
  async onLikeIconClick(event: Event): Promise<void> {
    event.stopPropagation(); // Prevent card click navigation
    
    if (!this.isLoggedIn) {
      // Emit event to show login modal (handled by parent)
      return;
    }

    if (!this.card?.id) {
      console.error('Cannot like: card ID is missing');
      return;
    }

    try {
      const actionType = this.isLiked ? 'UNLIKE' : 'LIKE';
      
      const payload = {
        assetId: this.card.id,
        assetType: 'DATABANK',
        actionType: actionType
      };

      await this.userInteractionApiService.createBookmarkOrLike(payload);
      
      // Update local state
      this.isLiked = !this.isLiked;
      this.likeChanged.emit(this.isLiked);
      
      // Update metrics count
      if (this.card.metrics) {
        if (this.isLiked) {
          this.card.metrics.likes = (this.card.metrics.likes || 0) + 1;
        } else {
          this.card.metrics.likes = Math.max(0, (this.card.metrics.likes || 0) - 1);
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }
}



